import React, { useRef, useCallback, forwardRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

type AccessibleModalProps = {
    children: React.ReactNode;
    className?: string;
    overlayClassName?: string;
    contentClassName?: string;
    initialFocusSelector?: string;
    contentStyle?: React.CSSProperties;
};

const defaultOverlayClass =
    'fade show d-block position-fixed top-0 start-0 w-100 h-100';
const defaultContentClass =
    'modal-content position-fixed top-50 start-50 translate-middle shadow-lg border-0 bg-white w-auto';
const defaultContentStyle: React.CSSProperties = {
    maxHeight: '100vh',
    maxWidth: 'min(960px, 100vw)',
    overflowY: 'auto',
    outline: 'none',
};
const defaultOverlayStyle: React.CSSProperties = {
    zIndex: 10000,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
};
const elevatedContentStyle: React.CSSProperties = { zIndex: 10010 };

const AccessibleModal: React.FC<AccessibleModalProps> = ({
    children,
    className = '',
    overlayClassName = defaultOverlayClass,
    contentClassName = defaultContentClass,
    initialFocusSelector,
    contentStyle,
}) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const lastActiveElementRef = useRef<HTMLElement | null>(null);

    const handleOpenAutoFocus = useCallback(
        (event: Event) => {
            if (
                !lastActiveElementRef.current &&
                document.activeElement instanceof HTMLElement
            ) {
                lastActiveElementRef.current = document.activeElement;
            }

            if (!initialFocusSelector || !contentRef.current) {
                return;
            }

            const target = contentRef.current.querySelector<HTMLElement>(
                initialFocusSelector,
            );

            if (target && typeof target.focus === 'function') {
                event.preventDefault();
                target.focus();
            }
        },
        [initialFocusSelector],
    );

    const handleCloseAutoFocus = useCallback((event: Event) => {
        const focusTarget = lastActiveElementRef.current;

        if (focusTarget) {
            event.preventDefault();
            focusTarget.focus();
            lastActiveElementRef.current = null;
        }
    }, []);

    return (
        <Dialog.Portal>
            <Dialog.Overlay
                className={overlayClassName}
                style={defaultOverlayStyle}
            />
            <Dialog.Content
                className={`${contentClassName} ${className}`.trim()}
                style={{
                    ...defaultContentStyle,
                    ...elevatedContentStyle,
                    ...contentStyle,
                }}
                ref={contentRef}
                onOpenAutoFocus={handleOpenAutoFocus}
                onCloseAutoFocus={handleCloseAutoFocus}
            >
                {children}
            </Dialog.Content>
        </Dialog.Portal>
    );
};

type CloseButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export const AccessibleModalCloseButton = forwardRef<
    HTMLButtonElement,
    CloseButtonProps
>(({ children, ...rest }, ref) => (
    <Dialog.Close asChild>
        <button type="button" {...rest} ref={ref}>
            {children}
        </button>
    </Dialog.Close>
));

AccessibleModalCloseButton.displayName = 'AccessibleModalCloseButton';

export default AccessibleModal;
